import React, { useEffect } from 'react';
import { arrayOf, bool, string } from 'prop-types';
import { compose } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { isMainSearchTypeKeywords } from '../../util/search';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import {
  Page,
  LayoutSingleColumn,
  ListingCard,
  Button,
  StrapiContentRenderer,
  SafeHtml,
  IconSpinner,
  NamedLink,
} from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  fetchSeoCategoryBySlug,
  getSeoCategory,
  getSeoListings,
  fetchSeoPagesSelector,
  getBlogsSelector,
  getStatesData,
} from '../../ducks/strapi.duck';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import {
  extractSeoCategoryData,
  isArrayLength,
  keepValidGermanChars,
} from '../../util/genericHelpers';
import { cityLabel, stateLabel } from '../../util/interlinking';
import {
  buildCityFaqs,
  buildCityQuickAnswer,
  buildCityTrailerTypeSections,
} from '../../util/stateSeoContent';
import RenderParagraphs from './RenderParagraphs';
import CityRelatedLinks from './CityRelatedLinks';

import css from './SeoCategoryPage.module.css';
import { convertMarkdownToReact } from '../../util/markdownHelpers';
import { DEFAULT_CONSTANTS } from '../../util/enums';
import { parse } from '../../util/urlHelpers';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import SectionHero from './SectionHero/SectionHero';
import Icons from '../../components/Icons/Icons';

// ─── Quick Answer Box ─────────────────────────────────────────────────────────

const QuickAnswerBox = ({ text }) => {
  if (!text) return null;
  return (
    <div className={css.quickAnswerBox}>
      <div className={css.quickAnswerLabel}>Quick Answer</div>
      <p className={css.quickAnswerText}>{text}</p>
    </div>
  );
};

// ─── FAQ Section ─────────────────────────────────────────────────────────────

const CityBreadcrumb = ({ stateSlug, stateName, cityLabelText }) => {
  if (!stateSlug || !stateName) return null;
  return (
    <nav className={css.topBreadcrumb} aria-label="Breadcrumb">
      <NamedLink name="LandingPage" className={css.topCrumb}>
        Home
      </NamedLink>
      <span className={css.topCrumbSep}>/</span>
      <NamedLink name="StateListsPage" params={{ state: stateSlug }} className={css.topCrumb}>
        {stateName} trailer rentals
      </NamedLink>
      {cityLabelText ? (
        <>
          <span className={css.topCrumbSep}>/</span>
          <span className={css.topCrumbCurrent}>{cityLabelText}</span>
        </>
      ) : null}
    </nav>
  );
};

const TrailerTypeSections = ({ sections, cityName }) => {
  if (!isArrayLength(sections)) return null;
  return (
    <div className={css.typeSections}>
      <h2 className={css.typeSectionsHeading}>
        Trailer Types Available in {cityName || 'This City'}
      </h2>
      {sections.map(section => (
        <div key={section.key} className={css.typeSection}>
          <h3 className={css.typeSectionHeading}>{section.heading}</h3>
          <p className={css.typeSectionBody}>{section.body}</p>
        </div>
      ))}
    </div>
  );
};

const FaqSection = ({ faqs }) => {
  if (!isArrayLength(faqs)) return null;
  return (
    <div className={css.faqSection}>
      <h2 className={css.faqHeading}>Frequently Asked Questions</h2>
      <dl className={css.faqList}>
        {faqs.map((faq, idx) => (
          <div key={idx} className={css.faqItem}>
            <dt className={css.faqQuestion}>{faq.question}</dt>
            <dd className={css.faqAnswer}>{faq.answer}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const SeoCategoryPageComponent = ({ scrollingDisabled, params, config, listings }) => {
  const dispatch = useDispatch();

  const title = params?.title || '';
  const stateSlugParam = params?.state || '';

  // SSR (loadData) populates the store on the server so listings + local
  // content are in the initial HTML for crawlers. This effect re-fetches on
  // client-side navigation (when loadData didn't run for this title).
  useEffect(() => {
    dispatch(fetchSeoCategoryBySlug({ title, config }));
  }, [title]);

  const seoCategory = useSelector(getSeoCategory);
  // Interlinking data (hydrated server-side in loadData).
  const allCategories = useSelector(fetchSeoPagesSelector) || [];
  const allBlogs = useSelector(getBlogsSelector) || [];
  const { statesData } = useSelector(getStatesData) || {};

  const {
    title: mainTitle,
    content = [],
    description = '',
    htmlDescription = '',
    headingImage = '',
    seoTitle,
    seoDescription,
  } = extractSeoCategoryData(seoCategory) || {};

  // Resolve the hero/social image: prefer a per-page Strapi headingImage, else
  // fall back to the default city-hub banner (headingImage is often '' because
  // city hubs omit it, and '' would otherwise leave a black hero + empty OG).
  const resolvedHeroImage = headingImage || DEFAULT_CONSTANTS.BANNER_IMAGE_CATEGORIES;

  // Guarantee a non-empty, SEO-friendly <title> server-side.
  const marketplaceName = config?.marketplaceName || 'BigLoads';
  const pageTitle =
    (seoTitle && seoTitle.trim()) ||
    (mainTitle && `${mainTitle.trim()} | ${marketplaceName}`) ||
    `Trailer Rentals | ${marketplaceName}`;

  // ── Resolve city/state names for FAQ & schema ──────────────────────────
  const norm = s => (typeof s === 'string' ? s.trim().toLowerCase() : '');
  const selfInList = Array.isArray(allCategories)
    ? allCategories.find(c => c && norm(c.slug) === norm(title))
    : null;
  const stateObj = (selfInList && selfInList.state) || seoCategory?.state || null;

  // City name: derive from Strapi title ("Trailer Rentals in Gilbert, Arizona" → "Gilbert")
  const rawCityLabel = cityLabel(selfInList || seoCategory);
  // Prefer just the city part (before the comma) for FAQ copy
  const cityName = rawCityLabel ? rawCityLabel.split(',')[0].trim() : title;

  // State name
  const stateName =
    stateLabel(stateObj) ||
    (stateSlugParam
      ? String(stateSlugParam)
          .replace(/^trailer-rentals-/, '')
          .split('-')
          .map(w => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ')
      : '');

  // ── Real price range from live listings (never fabricated) ──────────────
  // Pull actual per-day prices off the listings already loaded for this page
  // and, if we have at least two data points, express a real range for the
  // FAQ/quick-answer. If there are no listings, priceRange stays null and the
  // copy falls back to generic (no invented numbers).
  const priceRange = (() => {
    if (!isArrayLength(listings)) return null;
    const amounts = listings
      .map(l => l?.attributes?.price)
      .filter(p => p && typeof p.amount === 'number' && p.amount > 0)
      .map(p => p.amount); // minor units (cents)
    if (!amounts.length) return null;
    const min = Math.floor(Math.min(...amounts) / 100);
    const max = Math.floor(Math.max(...amounts) / 100);
    return min === max ? `$${min}/day` : `$${min}\u2013$${max}/day`;
  })();

  const listingCount = isArrayLength(listings) ? listings.length : 0;

  // ── City SEO content (templated + varied + real price data) ─────────────
  const cityOpts = { citySlug: title, priceRange };
  const quickAnswer =
    cityName && stateName ? buildCityQuickAnswer(cityName, stateName, cityOpts) : null;
  const faqs =
    cityName && stateName ? buildCityFaqs(cityName, stateName, cityOpts) : [];

  // Per-city trailer-type sections = pure editorial content (no live-inventory
  // claim), so we render all supported types on every city page.
  const trailerTypeSections =
    cityName && stateName
      ? buildCityTrailerTypeSections(cityName, stateName, {
          citySlug: title,
        })
      : [];

  // ── Structured data ─────────────────────────────────────────────────────
  const siteUrl = process.env.REACT_APP_MARKETPLACE_ROOT_URL || 'https://bigloads.com';
  const pageUrl = stateSlugParam
    ? `${siteUrl}/states/${stateSlugParam}/${title}`
    : `${siteUrl}/states/${title}`;

  const breadcrumbItems = [
    { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Trailer Rentals by State',
      item: `${siteUrl}/states`,
    },
  ];
  if (stateSlugParam && stateName) {
    breadcrumbItems.push({
      '@type': 'ListItem',
      position: 3,
      name: `Trailer Rentals in ${stateName}`,
      item: `${siteUrl}/states/${stateSlugParam}`,
    });
  }
  breadcrumbItems.push({
    '@type': 'ListItem',
    position: breadcrumbItems.length + 1,
    name: rawCityLabel || cityName,
    item: pageUrl,
  });

  const breadcrumbSchema = {
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems,
  };

  const faqSchema = isArrayLength(faqs)
    ? {
        '@type': 'FAQPage',
        mainEntity: faqs.map(faq => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      }
    : null;

  const webPageSchema = {
    '@type': 'WebPage',
    '@id': `${pageUrl}#webpage`,
    url: pageUrl,
    description: description || seoDescription,
    name: mainTitle || title,
    image: [resolvedHeroImage],
  };

  // LocalBusiness/Service schema — signals to Google this is a local service
  // page for "<city> trailer rental". areaServed = the city; provider = BigLoads.
  // Only emitted when we have a resolved city + state (never a bare/empty page).
  const localServiceSchema =
    cityName && stateName
      ? {
          '@type': 'Service',
          '@id': `${pageUrl}#service`,
          name: `Trailer Rentals in ${cityName}, ${stateName}`,
          serviceType: 'Trailer rental',
          provider: {
            '@type': 'Organization',
            name: marketplaceName,
            url: siteUrl,
          },
          areaServed: {
            '@type': 'City',
            name: cityName,
            containedInPlace: {
              '@type': 'State',
              name: stateName,
            },
          },
          url: pageUrl,
        }
      : null;

  const schemas = [webPageSchema, localServiceSchema, breadcrumbSchema, faqSchema].filter(
    Boolean
  );

  if (!seoCategory && !seoTitle) {
    return (
      <LayoutSingleColumn className={css.root} topbar={<TopbarContainer />} footer={<></>}>
        <div className={css.iconSpinner}>
          <IconSpinner />
        </div>
      </LayoutSingleColumn>
    );
  }

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={seoDescription}
      title={pageTitle}
      /* Explicit self-canonical: locks this city hub as THE ranking page for
         the "<city> trailer rental" term and prevents the /s?address= search
         results page from being treated as a competing duplicate. */
      canonicalUrl={pageUrl}
      facebookImages={[{ url: resolvedHeroImage, width: 1200, height: 630 }]}
      twitterImages={[
        {
          url: resolvedHeroImage.startsWith('http')
            ? resolvedHeroImage
            : `${config.marketplaceRootURL}${resolvedHeroImage}`,
          width: 600,
          height: 314,
        },
      ]}
      schema={schemas}
    >
      <LayoutSingleColumn
        className={css.root}
        topbar={<TopbarContainer isWhiteBgNav={false} isHeaderSticky={true} />}
        footer={<FooterContainer />}
      >
        <SectionHero bannerTitle={mainTitle} bannerImage={resolvedHeroImage} />
        <div className={css.pageRoot}>

          {/* Breadcrumb - directly under hero, above Quick Answer */}
          <CityBreadcrumb
            stateSlug={stateSlugParam}
            stateName={stateName}
            cityLabelText={rawCityLabel || cityName}
          />

          {/* Quick Answer box - near top, before editorial content */}
          <QuickAnswerBox text={quickAnswer} />

          <div className={css.taxDetailsSec}>
            <div className={css.taxDetailsLeft}>
              <div className={css.mapListingsCards}>
                {isArrayLength(listings)
                  ? listings.map(l => (
                      <ListingCard
                        className={css.listingCard}
                        key={l?.id?.uuid}
                        listing={l}
                        renderSizes={[
                          '(max-width: 549px) 100vw',
                          '(max-width: 767px) 50vw',
                          `(max-width: 1439px) 26vw`,
                          `(max-width: 1920px) 18vw`,
                          `14vw`,
                        ].join(', ')}
                        setActiveListing={() => {}}
                      />
                    ))
                  : null}
              </div>

              {/* Graceful empty state - content still ranks; give renters a
                  next step and owners a reason to list. Only shown when the
                  page genuinely has zero live listings. */}
              {listingCount === 0 ? (
                <div className={css.emptyListings}>
                  <h2 className={css.emptyListingsHeading}>
                    No trailers listed in {cityName || 'this city'} yet
                  </h2>
                  <p className={css.emptyListingsText}>
                    BigLoads is growing across {stateName || 'the country'}. There
                    aren't any active listings right here at the moment - but new
                    trailers are added all the time. Try browsing nearby cities,
                    or list your own trailer and be the first owner in{' '}
                    {cityName || 'your area'}.
                  </p>
                  <div className={css.emptyListingsCtas}>
                    <NamedLink name="NewListingPage" className={css.emptyListingsPrimary}>
                      List your trailer
                    </NamedLink>
                    <NamedLink name="SearchPage" className={css.emptyListingsSecondary}>
                      Browse all trailers
                    </NamedLink>
                  </div>
                </div>
              ) : null}
              <div className={css.pageHeader}>
                {htmlDescription ? (
                  <SafeHtml html={htmlDescription} />
                ) : description ? (
                  <StrapiContentRenderer content={description} />
                ) : null}
              </div>
              <div className={css.taxDetailsContent}>
                {isArrayLength(content)
                  ? content.map(c => <RenderParagraphs key={c?.id} content={c} />)
                  : null}
              </div>
            </div>
          </div>

          {/* Per-trailer-type H2 sections - real-inventory-led, keyword-rich,
              between editorial and FAQ. Cascades to every city hub. */}
          <TrailerTypeSections sections={trailerTypeSections} cityName={cityName} />

          {/* FAQ section - below Strapi editorial, above related links */}
          <FaqSection faqs={faqs} />

          <CityRelatedLinks
            category={seoCategory}
            citySlug={title}
            stateSlugParam={params?.state}
            categories={allCategories}
            blogs={allBlogs}
            states={statesData || []}
          />
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

SeoCategoryPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  intl: intlShape.isRequired,
};

const EnhancedSeoCategoryPage = props => {
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const history = useHistory();
  const params = useParams();
  const intl = useIntl();

  return (
    <SeoCategoryPageComponent
      routeConfiguration={routeConfiguration}
      marketplaceName={config.marketplaceName}
      isKeywordSearch={isMainSearchTypeKeywords(config)}
      history={history}
      params={params}
      config={config}
      intl={intl}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { currentPageResultIds } = state.strapi;
  const listings = getListingsById(state, currentPageResultIds);

  return {
    listings,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const SeoCategoryPage = compose(connect(mapStateToProps))(EnhancedSeoCategoryPage);

export default SeoCategoryPage;
